import { useState, useEffect, useCallback } from 'react';
import { 
  requestNotificationPermission, 
  onForegroundMessage, 
  isNotificationSupported,
  getNotificationPermissionStatus,
  getPlatformInfo 
} from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook for managing push notifications
 */
export const usePushNotifications = () => {
  const { user, token } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [fcmToken, setFcmToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check support on mount
  useEffect(() => {
    const platformInfo = getPlatformInfo();
    setIsSupported(platformInfo.supported);
    setPermission(getNotificationPermissionStatus());
  }, []);

  // Register FCM token with backend
  const registerToken = useCallback(async (newToken) => {
    if (!token || !newToken) return false;

    try {
      await axios.post(
        `${API}/api/notifications/register-token`,
        { fcm_token: newToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('FCM token registered with backend');
      return true;
    } catch (error) {
      console.error('Failed to register FCM token:', error);
      return false;
    }
  }, [token]);

  // Request permission and get token
  const enableNotifications = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    setIsLoading(true);
    try {
      const newToken = await requestNotificationPermission();
      
      if (newToken) {
        setFcmToken(newToken);
        setPermission('granted');
        
        // Register with backend
        const registered = await registerToken(newToken);
        
        if (registered) {
          toast.success('Push notifications enabled!');
        }
        return true;
      } else {
        setPermission(Notification.permission);
        if (Notification.permission === 'denied') {
          toast.error('Notification permission denied. Please enable in browser settings.');
        }
        return false;
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error('Failed to enable notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, registerToken]);

  // Unregister token
  const disableNotifications = useCallback(async () => {
    if (!token || !fcmToken) return;

    try {
      await axios.delete(
        `${API}/api/notifications/unregister-token`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          data: { fcm_token: fcmToken }
        }
      );
      setFcmToken(null);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Failed to unregister FCM token:', error);
    }
  }, [token, fcmToken]);

  // Set up foreground message listener
  useEffect(() => {
    if (!isSupported || permission !== 'granted') return;

    const unsubscribe = onForegroundMessage((payload) => {
      // Show toast notification for foreground messages
      const { title, body } = payload.notification || {};
      
      if (title) {
        toast(title, {
          description: body,
          action: {
            label: 'View',
            onClick: () => {
              // Navigate based on notification type
              const type = payload.data?.type;
              if (type === 'new_referral') {
                window.location.href = '/consultant/dashboard';
              } else {
                window.location.href = '/dashboard';
              }
            }
          }
        });
      }
    });

    return () => unsubscribe();
  }, [isSupported, permission]);

  // Auto-enable on login if previously granted
  useEffect(() => {
    if (user && permission === 'granted' && !fcmToken && isSupported) {
      enableNotifications();
    }
  }, [user, permission, fcmToken, isSupported, enableNotifications]);

  return {
    isSupported,
    permission,
    fcmToken,
    isLoading,
    enableNotifications,
    disableNotifications,
    isEnabled: permission === 'granted' && !!fcmToken
  };
};

export default usePushNotifications;
